/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * The Table component for column to expand row
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @version 1.3.1
 * @since 1.1.0
 */

import { Fragment } from 'react';

import { FilterStubStyled } from '../../styled/TableStyled';
import Filter from '../Filters/Filter';
import { CellStyled, TableExpandButtonStyled } from './styled/ColumnsStyled';

/**
 * @description Props for expanded column
 * @interface IExpandedColumn
 */
export interface IExpandedColumn {
    collapseTableSubRows: boolean;
    onExpandRow: (payload: number) => void;
    onCollapseRow: (payload: number) => void;
}

/**
 * @description Cell constructor return function
 * @param {(payload: number) => void} onCollapseRow - collapse row callback
 * @param {(payload: number) => void} onExpandRow - expand row callback
 * @param collapseTableSubRows: boolean - check if sub row should be collapsed
 */
export const CellConstruct = (collapseTableSubRows: boolean, onCollapseRow: (payload: number) => void,
                              onExpandRow: (payload: number) => void) =>
    ({ row }: any) => {
    const clickHandler = (): void => {
        if (row.isExpanded) {
            onCollapseRow(row.original.id)
        } else {
            onExpandRow(row.original.id);
        }
    };

    if (row.isExpanded && collapseTableSubRows) {
        row.toggleRowExpanded();
    }

    return (
        <Fragment>
            {
                row.canExpand ?
                    <span
                        {
                            ...row.getToggleRowExpandedProps({
                                style: {
                                    paddingLeft: `${row.depth * 2}rem`,
                                },
                            })
                        }
                        data-button="expand-button"
                    >
                    <TableExpandButtonStyled
                        className={!row.isExpanded ? 'plus' : 'minus'}
                        onClick={clickHandler}
                    >
                        <div className="horizontal-line"/>
                        <div className="vertical-line"/>
                    </TableExpandButtonStyled>
                </span>
                    : null
            }
        </Fragment>
    );
};

/**
 * @description Header construct return function
 */
export const HeaderConstruct = () => () => (
    <CellStyled>
        <Filter
            isSortable={false}
            name=""
            sort=""
            onSort={() => {
            }}
        >
            <FilterStubStyled/>
        </Filter>
    </CellStyled>
);
/**
 * @description setting the expanded column
 * @interface IExpandedColumn
 */
const ExpandedColumn = ({ collapseTableSubRows, onCollapseRow, onExpandRow }: IExpandedColumn) => ({
    Cell: CellConstruct(collapseTableSubRows, onCollapseRow, onExpandRow),
    fixed: 'left',
    Footer: () => <Fragment/>,
    Header: HeaderConstruct(),
    id: 'expander',
    resizable: false,
    rowCount: 1,
    width: 48,
});

export default ExpandedColumn;
